import React, { Component } from 'react';
import SakuraOrbit from './SakuraOrbit';
import { Redirect } from 'react-router-dom';
import { DataRPC } from '../../rpc-client/DataRPC';
import SakuraSep from './SakuraSep';
import BurstBtn from './BurstBtn';

const rpc = new DataRPC();
const LIMIT = 12;
const SWAY_DURATIONS = [3.1, 4.7, 5.4, 3.8, 6.0, 4.2, 5.0, 3.5, 4.9, 6.3];
const SWAY_ANGLES = [1.6, 2.4, 1.9, 2.8, 1.4, 2.2];
const PW_KEY = (id) => `ema_pw_${id}`;

function formatDate(str) {
    if (!str) return '';
    const d = new Date(str);
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}

function formatTime(str) {
    if (!str) return '';
    const d = new Date(str);
    return `${formatDate(str)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function storeGet(key) { try { return window.localStorage.getItem(key); } catch { return null; } }
function storeSet(key, val) { try { window.localStorage.setItem(key, val); } catch { /* private mode */ } }

class EmaWall extends Component {
    state = {
        wishes: [], total: 0, page: 1,
        loading: true, loadingMore: false,
        myOpenId: null,

        // create
        showForm: false, name: '', message: '',
        submitting: false, error: null, openIdConflict: null,
        created: null,          // { id, password } — shown once in a modal
        copied: false,

        // detail
        detail: null,
        comments: [], commentsLoading: false,
        cName: '', cMsg: '', cSubmitting: false, cError: null,
        cooldownUntil: 0, now: Date.now(),
        completePwd: '', completing: false, completeError: null,

        celebrate: false,
        pop: null,              // { id, n } — replays the 🌸 pop animation
    };

    reacting = {};

    // Optimistic 🌸 toggle; the server's count wins once it answers.
    reactEma = (e, wish) => {
        e.stopPropagation();
        const id = wish.id;
        if (this.reacting[id]) return;
        this.reacting[id] = true;

        const patch = (fn) => this.setState(prev => ({
            wishes: prev.wishes.map(w => w.id === id ? fn(w) : w),
            detail: prev.detail && prev.detail.id === id ? fn(prev.detail) : prev.detail,
        }));
        const before = { reacted: !!wish.reacted, reaction_count: wish.reaction_count || 0 };
        const nowOn = !before.reacted;

        patch(w => ({ ...w, reacted: nowOn, reaction_count: Math.max(0, (w.reaction_count || 0) + (nowOn ? 1 : -1)) }));
        if (nowOn) this.setState(prev => ({ pop: { id, n: ((prev.pop && prev.pop.n) || 0) + 1 } }));

        rpc.fetchRawData('ReactEma', { id })
            .then(res => {
                if (res && res.success) patch(w => ({ ...w, reacted: res.reacted, reaction_count: res.count }));
                else patch(w => ({ ...w, ...before }));
            })
            .catch(() => patch(w => ({ ...w, ...before })))
            .finally(() => { delete this.reacting[id]; });
    };

    componentDidMount() {
        this.fetchWishes(1);
        this.tick = setInterval(() => {
            if (this.state.cooldownUntil > Date.now() || this.state.now < this.state.cooldownUntil) {
                this.setState({ now: Date.now() });
            }
        }, 1000);
        this.onKey = (e) => { if (e.key === 'Escape') this.closeModals(); };
        window.addEventListener('keydown', this.onKey);
    }

    componentWillUnmount() {
        clearInterval(this.tick);
        clearTimeout(this.celebrateTimer);
        window.removeEventListener('keydown', this.onKey);
        document.body.style.overflow = '';
    }

    componentDidUpdate(_, prev) {
        const open = !!(this.state.detail || this.state.created);
        const wasOpen = !!(prev.detail || prev.created);
        if (open !== wasOpen) document.body.style.overflow = open ? 'hidden' : '';
    }

    fetchWishes(page) {
        const isFirst = page === 1;
        this.setState(isFirst ? { loading: true } : { loadingMore: true });
        rpc.fetchRawData('GetEma', { page, limit: LIMIT })
            .then(res => {
                const data = (res && res.data) || [];
                this.setState(prev => ({
                    wishes:      isFirst ? data : [...prev.wishes, ...data],
                    total:       (res && res.total) || 0,
                    myOpenId:    res ? res.my_open_id || null : prev.myOpenId,
                    page,
                    loading:     false,
                    loadingMore: false,
                }));
            })
            .catch(() => this.setState({ loading: false, loadingMore: false }));
    }

    // ── Create ───────────────────────────────────────────────────────────
    submit = () => {
        const { name, message } = this.state;
        if (!name.trim() || !message.trim()) {
            this.setState({ error: 'Vui lòng điền tên và lời ước.' });
            return;
        }
        this.setState({ submitting: true, error: null, openIdConflict: null });
        rpc.fetchRawData('InsertEma', { name: name.trim(), message: message.trim() })
            .then(res => {
                if (res && res.success) {
                    storeSet(PW_KEY(res.id), res.password);
                    this.setState({
                        submitting: false, showForm: false, name: '', message: '',
                        created: { id: res.id, password: res.password }, copied: false,
                    });
                    this.fetchWishes(1);
                } else {
                    this.setState({
                        submitting: false,
                        error: (res && res.message) || 'Có lỗi xảy ra.',
                        openIdConflict: res && res.code === 'HAS_OPEN' ? res.open_id : null,
                    });
                }
            })
            .catch(() => this.setState({ submitting: false, error: 'Có lỗi xảy ra.' }));
    };

    copyPassword = () => {
        const pw = this.state.created && this.state.created.password;
        if (!pw) return;
        const done = () => this.setState({ copied: true });
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(pw).then(done).catch(done);
        } else {
            done();
        }
    };

    // ── Detail / comments / complete ─────────────────────────────────────
    openDetail = (wish) => {
        if (!wish) return;
        this.setState({
            detail: wish, comments: [], commentsLoading: true,
            cError: null, completeError: null,
            completePwd: storeGet(PW_KEY(wish.id)) || '',
        });
        rpc.fetchRawData('GetEmaComments', { ema_id: wish.id })
            .then(res => this.setState({ comments: Array.isArray(res) ? res : [], commentsLoading: false }))
            .catch(() => this.setState({ commentsLoading: false }));
    };

    openDetailById = (id) => {
        const w = this.state.wishes.find(x => String(x.id) === String(id));
        this.setState({ created: null });
        if (w) this.openDetail(w);
    };

    closeModals = () => this.setState({ detail: null, created: null });

    submitComment = () => {
        const { detail, cName, cMsg, cooldownUntil } = this.state;
        if (!cMsg.trim()) { this.setState({ cError: 'Vui lòng nhập bình luận.' }); return; }
        if (cooldownUntil > Date.now()) return;
        this.setState({ cSubmitting: true, cError: null });
        rpc.fetchRawData('InsertEmaComment', { ema_id: detail.id, name: cName.trim(), message: cMsg.trim() })
            .then(res => {
                if (res && res.success) {
                    const until = Date.now() + 3 * 60 * 1000;
                    this.setState(prev => ({
                        cSubmitting: false, cMsg: '', cooldownUntil: until, now: Date.now(),
                        comments: [...prev.comments, {
                            id: `local-${Date.now()}`, name: prev.cName.trim() || null,
                            message: prev.cMsg.trim(), created_at: new Date().toISOString(),
                        }],
                        wishes: prev.wishes.map(w => w.id === detail.id ? { ...w, comment_count: (w.comment_count || 0) + 1 } : w),
                    }));
                } else if (res && res.code === 'COOLDOWN') {
                    this.setState({ cSubmitting: false, cooldownUntil: Date.now() + res.wait * 1000, now: Date.now(), cError: res.message });
                } else {
                    this.setState({ cSubmitting: false, cError: (res && res.message) || 'Có lỗi xảy ra.' });
                }
            })
            .catch(() => this.setState({ cSubmitting: false, cError: 'Có lỗi xảy ra.' }));
    };

    completeWish = () => {
        const { detail, completePwd } = this.state;
        if (!completePwd.trim()) { this.setState({ completeError: 'Vui lòng nhập mật khẩu.' }); return; }
        this.setState({ completing: true, completeError: null });
        rpc.fetchRawData('CompleteEma', { id: detail.id, password: completePwd.trim() })
            .then(res => {
                if (res && res.success) {
                    const now = new Date().toISOString();
                    this.setState(prev => ({
                        completing: false, celebrate: true,
                        detail: { ...prev.detail, status: 'granted', completed_at: now },
                        wishes: prev.wishes.map(w => w.id === detail.id ? { ...w, status: 'granted', completed_at: now } : w),
                        myOpenId: String(prev.myOpenId) === String(detail.id) ? null : prev.myOpenId,
                    }));
                    clearTimeout(this.celebrateTimer);
                    this.celebrateTimer = setTimeout(() => this.setState({ celebrate: false }), 3200);
                } else {
                    this.setState({ completing: false, completeError: (res && res.message) || 'Có lỗi xảy ra.' });
                }
            })
            .catch(() => this.setState({ completing: false, completeError: 'Có lỗi xảy ra.' }));
    };

    // ── Render pieces ────────────────────────────────────────────────────
    renderCard(w, i) {
        const granted = w.status === 'granted';
        const mine = String(w.id) === String(this.state.myOpenId);
        return (
            <div
                role="button"
                tabIndex={0}
                key={w.id}
                className={`ema-card${granted ? ' ema-card--granted' : ''}${mine ? ' ema-card--mine' : ''}`}
                onClick={() => this.openDetail(w)}
                onKeyDown={e => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); this.openDetail(w); } }}
                style={{
                    '--ema-enter-delay': `${(i % LIMIT) * 0.07}s`,
                    '--ema-duration':    `${SWAY_DURATIONS[i % SWAY_DURATIONS.length]}s`,
                    '--ema-angle':       `${SWAY_ANGLES[i % SWAY_ANGLES.length]}deg`,
                }}
            >
                <span className="ema-card-swing">
                    <span className="ema-card-rope" />
                    <span className="ema-card-board">
                        <span className="ema-card-hole" />
                        {mine && <span className="ema-card-ribbon">Của bạn</span>}
                        <span className="ema-card-seal">{granted ? '叶' : '願'}</span>
                        <span className="ema-card-message">{w.message}</span>
                        <span className="ema-card-footer">
                            <span className="ema-card-name">— {w.name}</span>
                            <span className="ema-card-meta">
                                {w.comment_count > 0 && <span className="ema-card-comments">💬 {w.comment_count}</span>}
                                <span className="ema-card-date">{formatDate(w.created_at)}</span>
                                {this.renderReact(w)}
                            </span>
                        </span>
                        {granted && <span className="ema-card-stamp">叶<small>Đã thành hiện thực</small></span>}
                    </span>
                </span>
            </div>
        );
    }

    renderReact(w, big = false) {
        const { pop } = this.state;
        const popping = pop && pop.id === w.id;
        return (
            <button
                type="button"
                className={`ema-react${w.reacted ? ' ema-react--on' : ''}${big ? ' ema-react--big' : ''}`}
                onClick={e => this.reactEma(e, w)}
                onKeyDown={e => e.stopPropagation()}
                aria-pressed={!!w.reacted}
                aria-label={w.reacted ? 'Bỏ thả hoa' : 'Thả hoa cho lời ước này'}
                title={w.reacted ? 'Bỏ thả hoa' : 'Thả hoa 🌸'}
            >
                <span className="ema-react-icon" key={popping ? `p${pop.n}` : 'idle'}>🌸</span>
                <span className="ema-react-count">{w.reaction_count || 0}</span>
                {popping && w.reacted && <span className="ema-react-float" key={`f${pop.n}`}>🌸</span>}
            </button>
        );
    }

    renderCreatedModal() {
        const { created, copied } = this.state;
        if (!created) return null;
        return (
            <div className="ema-modal-backdrop" onClick={this.closeModals}>
                <div className="ema-modal ema-modal--pw" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
                    <button className="ema-modal-close" onClick={this.closeModals} aria-label="Đóng">✕</button>
                    <div className="ema-pw-kanji">願</div>
                    <h3 className="ema-modal-title">Lời ước đã được treo lên bảng 🌸</h3>
                    <p className="ema-pw-lead">Đây là <strong>mật khẩu</strong> cho lời ước của bạn:</p>
                    <div className="ema-pw-box">
                        <span className="ema-pw-word">{created.password}</span>
                        <button className="ema-pw-copy" onClick={this.copyPassword}>{copied ? '✓ Đã chép' : 'Sao chép'}</button>
                    </div>
                    <p className="ema-pw-note">
                        Hãy ghi nhớ hoặc chụp lại mật khẩu này. Khi lời ước của bạn <strong>thành hiện thực</strong>,
                        quay lại đây, mở lời ước và nhập mật khẩu để đánh dấu <strong>叶 · Đã thành hiện thực</strong>.
                    </p>
                    <p className="ema-pw-note ema-pw-note--sub">
                        Mỗi người chỉ treo được một lời ước tại một thời điểm — bạn có thể treo lời ước mới sau khi lời ước này đã thành hiện thực.
                    </p>
                    <div className="ema-form-actions">
                        <button className="ema-cancel-btn" onClick={this.closeModals}>Đóng</button>
                        <button className="ema-submit-btn" onClick={() => this.openDetailById(created.id)}>Xem lời ước của tôi</button>
                    </div>
                </div>
            </div>
        );
    }

    renderDetailModal() {
        const { detail, comments, commentsLoading, cName, cMsg, cSubmitting, cError,
            cooldownUntil, now, completePwd, completing, completeError, celebrate } = this.state;
        if (!detail) return null;
        const granted = detail.status === 'granted';
        const wait = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));
        const waitLabel = `${Math.floor(wait / 60)}:${String(wait % 60).padStart(2, '0')}`;

        return (
            <div className="ema-modal-backdrop" onClick={this.closeModals}>
                <div className="ema-modal ema-modal--detail" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
                    <button className="ema-modal-close" onClick={this.closeModals} aria-label="Đóng">✕</button>

                    {celebrate && (
                        <div className="ema-confetti" aria-hidden="true">
                            {Array.from({ length: 28 }, (_, k) => (
                                <span key={k} style={{
                                    '--x': `${(k * 37) % 100}%`,
                                    '--d': `${(k % 7) * 0.12}s`,
                                    '--r': `${(k * 53) % 360}deg`,
                                }}>🌸</span>
                            ))}
                        </div>
                    )}

                    <div className={`ema-detail-board${granted ? ' ema-detail-board--granted' : ''}`}>
                        <span className="ema-card-hole" />
                        <span className="ema-card-seal">{granted ? '叶' : '願'}</span>
                        <p className="ema-detail-message">{detail.message}</p>
                        <div className="ema-card-footer">
                            <span className="ema-card-name">— {detail.name}</span>
                            <span className="ema-card-meta">
                                <span className="ema-card-date">{formatDate(detail.created_at)}</span>
                                {this.renderReact(detail, true)}
                            </span>
                        </div>
                        {granted && (
                            <span className="ema-card-stamp ema-card-stamp--big">叶<small>Đã thành hiện thực{detail.completed_at ? ` · ${formatDate(detail.completed_at)}` : ''}</small></span>
                        )}
                    </div>

                    {!granted && (
                        <div className="ema-complete">
                            <p className="ema-complete-title">Lời ước này đã thành hiện thực?</p>
                            <div className="ema-complete-row">
                                <input
                                    className="ema-input"
                                    placeholder="Mật khẩu của lời ước"
                                    value={completePwd}
                                    autoComplete="off"
                                    onChange={e => this.setState({ completePwd: e.target.value })}
                                    onKeyDown={e => { if (e.key === 'Enter') this.completeWish(); }}
                                />
                                <button className="ema-submit-btn" onClick={this.completeWish} disabled={completing}>
                                    {completing ? 'Đang xử lý...' : '叶 Đánh dấu'}
                                </button>
                            </div>
                            {completeError && <div className="ema-form-error">⚠ {completeError}</div>}
                        </div>
                    )}

                    <div className="ema-comments">
                        <h4 className="ema-comments-title">💬 Bình luận ({comments.length})</h4>
                        {commentsLoading ? (
                            <div className="ema-comments-empty">Đang tải...</div>
                        ) : comments.length === 0 ? (
                            <div className="ema-comments-empty">Chưa có bình luận. Gửi lời động viên đầu tiên nhé!</div>
                        ) : (
                            <ul className="ema-comment-list">
                                {comments.map(c => (
                                    <li key={c.id} className="ema-comment">
                                        <div className="ema-comment-head">
                                            <span className="ema-comment-name">{c.name || 'Người lạ'}</span>
                                            <span className="ema-comment-time">{formatTime(c.created_at)}</span>
                                        </div>
                                        <p className="ema-comment-msg">{c.message}</p>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <div className="ema-comment-form">
                            <input
                                className="ema-input"
                                placeholder="Tên (không bắt buộc)"
                                value={cName}
                                maxLength={50}
                                onChange={e => this.setState({ cName: e.target.value })}
                            />
                            <textarea
                                className="ema-textarea"
                                placeholder="Gửi lời động viên..."
                                value={cMsg}
                                maxLength={300}
                                rows={3}
                                onChange={e => this.setState({ cMsg: e.target.value })}
                            />
                            {cError && wait === 0 && <div className="ema-form-error">⚠ {cError}</div>}
                            <div className="ema-comment-actions">
                                <span className="ema-char-count">{cMsg.length} / 300</span>
                                <button
                                    className="ema-submit-btn"
                                    onClick={this.submitComment}
                                    disabled={cSubmitting || wait > 0}
                                >
                                    {wait > 0 ? `Chờ ${waitLabel}` : cSubmitting ? 'Đang gửi...' : 'Gửi bình luận'}
                                </button>
                            </div>
                            <p className="ema-comment-hint">Không cần đăng nhập · tối đa 1 bình luận mỗi 3 phút.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        if (!this.props.location?.state?.allowed) return <Redirect to="/not-found" />;

        const { wishes, total, loading, loadingMore, myOpenId, showForm, name, message,
            submitting, error, openIdConflict } = this.state;
        const hasMore = wishes.length < total;

        return (
            <>
                <div className="page-header ema-page-header">
                    <div className="page-header-overlay ema-header-overlay" />
                    <div className="container page-header-body">
                        <div className="row">
                            <div className="col-12">
                                <div className="entry-meta-date">絵馬 · Cộng đồng Yosakoi</div>
                                <h1 className="page-header-title">WISHES</h1>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="sep-gold-rule" style={{ margin: 0 }} />

                <div className="ema-page">
                    <div className="container">

                        <div className="entry-title">
                            <p>絵馬 · EMA</p>
                            <h2>GỬI LỜI CHÚC &amp; ĐIỀU ƯỚC</h2>
                        </div>

                        <div className="ema-intro">
                            <p className="ema-intro-main">
                                Chúc mừng bạn đã rảnh rỗi mà tìm được page này 🎉 Hãy viết lời ước trong năm của bạn — be it your love wish for a girl in other team or standing in front when performance <em>(something that you can never archive)</em>, you can log it here and <strong>We don't judge it.</strong>
                            </p>
                            <p className="ema-intro-sub">
                                Khi treo lời ước, bạn sẽ nhận một <strong>mật khẩu</strong>. Để nó tòng teng theo gió, nhận lời động viên từ những người xa lạ — và quay lại nhập mật khẩu để đánh dấu <strong>叶 thành hiện thực</strong> khi điều ước đã đến 🌸
                            </p>
                        </div>

                        {/* Create */}
                        <div className="ema-cta-wrap">
                            {myOpenId && !showForm ? (
                                <div className="ema-mine-note">
                                    <span>🎋 Bạn đang có một lời ước treo trên bảng. Hãy đánh dấu nó thành hiện thực trước khi treo lời ước mới.</span>
                                    <button className="ema-link-btn" onClick={() => this.openDetailById(myOpenId)}>Xem lời ước của tôi →</button>
                                </div>
                            ) : !showForm ? (
                                <button className="ema-write-btn" onClick={() => this.setState({ showForm: true, error: null, openIdConflict: null })}>
                                    ✍️ &nbsp;Viết Lời Ước
                                </button>
                            ) : (
                                <div className="ema-form">
                                    <div className="ema-form-kanji">願</div>
                                    <h3 className="ema-form-title">Ghi lời ước của bạn</h3>
                                    <input
                                        className="ema-input"
                                        placeholder="Tên của bạn *"
                                        value={name}
                                        maxLength={50}
                                        onChange={e => this.setState({ name: e.target.value })}
                                    />
                                    <textarea
                                        className="ema-textarea"
                                        placeholder="Lời ước / lời chúc của bạn... (tối đa 200 ký tự)"
                                        value={message}
                                        maxLength={200}
                                        rows={4}
                                        onChange={e => this.setState({ message: e.target.value })}
                                    />
                                    <div className="ema-char-count">{message.length} / 200</div>
                                    {error && (
                                        <div className="ema-form-error">
                                            ⚠ {error}
                                            {openIdConflict && (
                                                <button className="ema-link-btn" onClick={() => this.openDetailById(openIdConflict)}>Xem lời ước của tôi →</button>
                                            )}
                                        </div>
                                    )}
                                    <div className="ema-form-actions">
                                        <button className="ema-cancel-btn" onClick={() => this.setState({ showForm: false, error: null })}>
                                            Hủy
                                        </button>
                                        <button className="ema-submit-btn" onClick={this.submit} disabled={submitting}>
                                            {submitting ? '🌸 Đang gửi...' : '🌸 Treo lên bảng'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Wall */}
                        {loading ? (
                            <SakuraOrbit label="Đang tải lời ước" />
                        ) : wishes.length === 0 ? (
                            <div className="ema-empty">
                                <div className="ema-empty-kanji">願</div>
                                <p>Chưa có lời ước nào. Hãy là người đầu tiên!</p>
                            </div>
                        ) : (
                            <div className="ema-wall">
                                <div className="ema-beam" aria-hidden="true" />
                                <div className="ema-grid">
                                    {wishes.map((w, i) => this.renderCard(w, i))}
                                </div>
                            </div>
                        )}

                        {hasMore && !loading && (
                            <div className="nws-load-more">
                                <BurstBtn
                                    className="btn btn-img-base btn-img-down"
                                    onClick={() => this.fetchWishes(this.state.page + 1)}
                                    disabled={loadingMore}
                                >
                                    {loadingMore ? 'Đang tải...' : 'Xem thêm lời ước'}
                                </BurstBtn>
                            </div>
                        )}

                        <div className="ema-total-note">
                            {total > 0 && `${total} lời ước đã được gửi 🌸`}
                        </div>

                        <SakuraSep />
                    </div>
                </div>

                <div className="sep-gold-rule" style={{ margin: 0 }} />

                {this.renderCreatedModal()}
                {this.renderDetailModal()}
            </>
        );
    }
}

export default EmaWall;
